import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { AttachmentsService } from '@kinet-open-network/client/attachments/data-access';

const ATTACHMENT_LOADING_DATA_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='120' height='80' viewBox='0 0 120 80'><rect width='120' height='80' fill='#f3f4f6'/><circle cx='60' cy='40' r='12' fill='none' stroke='#9ca3af' stroke-width='4' stroke-linecap='round' stroke-dasharray='52 24'><animateTransform attributeName='transform' type='rotate' from='0 60 40' to='360 60 40' dur='1s' repeatCount='indefinite'/></circle></svg>",
)}`;

type AttachmentInfo =
  | { state: 'loading' | 'error' }
  | { state: 'loaded'; dataUrl: string };

@Component({
  selector: 'kon-quill-editor-content',
  standalone: true,
  imports: [CommonModule],
  template: `<div [innerHTML]="sanitizedContent()"></div>`,
  styles: `
  :host::ng-deep {
    .ql-align-center {
      place-self: center;
    }

    .ql-align-right {
      place-self: end;
    }

    .ql-align-justify {
      text-align: justify;
    }

    ol {
      list-style-type: decimal;
      padding-left: 1.5rem;
    }

    ul {
      list-style-type: disc;
      padding-left: 1.5rem;
    }

    img.kon-loading-attachment {
      min-height: 2.5rem;
      background-color: rgba(0, 0, 0, 0.05);

      &:after {
        content: '';
        position: absolute;
        width: 1.5rem;
        height: 1.5rem;
        border: 4px solid #e5e7eb;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
  }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuillEditorContentComponent {
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private attachmentsService = inject(AttachmentsService);
  private attachmentsMap = signal(new Map<string, AttachmentInfo>());

  content = input<string | undefined>('');

  constructor() {
    // When content changes, load any new attachment IDs in parallel.
    effect(() => {
      const content = this.content() || '';
      if (!content.includes('/attachments/')) return;

      const ids = this.parseAttachmentIds(content);
      const map = untracked(() => this.attachmentsMap());
      const newIds = ids.filter((id) => !map.has(id));

      if (newIds.length === 0) return;

      untracked(() => {
        this.attachmentsMap.update((current) => {
          const next = new Map(current);
          newIds.forEach((id) => next.set(id, { state: 'loading' }));
          return next;
        });

        newIds.forEach((id) => {
          this.attachmentsService
            .getById(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (attachment) => {
                const dataUrl = `data:${attachment.mimeType};base64,${attachment.data}`;
                this.attachmentsMap.update((map) =>
                  new Map(map).set(id, { state: 'loaded', dataUrl }),
                );
              },
              error: () => {
                this.attachmentsMap.update((map) =>
                  new Map(map).set(id, { state: 'error' }),
                );
              },
            });
        });
      });
    });
  }

  sanitizedContent = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.renderedContent()),
  );

  renderedContent = computed(() => {
    const content = this.content() || '';
    const attachmentsMap = this.attachmentsMap();

    if (!content.includes('/attachments/')) {
      return content;
    }

    const parserHost = document.createElement('div');
    parserHost.innerHTML = content;

    const imageEntries = Array.from(
      parserHost.querySelectorAll<HTMLImageElement>('img[src]'),
    )
      .map((element) => {
        const src = element.getAttribute('src') || '';
        const id = this.extractAttachmentId(src);
        return { element, id, src };
      })
      .filter(
        (
          entry,
        ): entry is { element: HTMLImageElement; id: string; src: string } =>
          Boolean(entry.id),
      );

    if (imageEntries.length === 0) {
      return content;
    }

    for (const { element, id, src } of imageEntries) {
      const info = attachmentsMap.get(id);

      if (info?.state === 'loaded') {
        element.setAttribute('src', info.dataUrl);
      } else if (info?.state === 'error') {
        element.classList.add('kon-attachment-error');
        element.setAttribute('src', src);
      } else {
        // queued, loading, or not yet seen — show placeholder
        element.classList.add('kon-loading-attachment');
        element.setAttribute('src', ATTACHMENT_LOADING_DATA_URL);
      }
    }

    return parserHost.innerHTML;
  });

  private parseAttachmentIds(content: string): string[] {
    return [
      ...new Set(
        [...content.matchAll(/\/attachments\/([0-9a-f-]{36})/gi)].map(
          (m) => m[1],
        ),
      ),
    ];
  }

  private extractAttachmentId(src: string): string | null {
    return src.match(/\/attachments\/([0-9a-f-]{36})/i)?.[1] ?? null;
  }
}
